import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Button,
  TableBody,
  Snackbar,
} from '@mui/material';
import {
  Web as WebIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as PreviewIcon,
  BarChart as AnalyticsIcon,
  ModeEdit as EditIcon,
  Delete as DeleteIcon,
  MoreHoriz as MoreVertIcon,
  CleaningServices as CleaningServicesIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSites } from '../../hooks/useSites';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useUsersCache } from '../../hooks/useUsersCache';
import { LandingPage, User } from '../../types';
import { SiteService } from '../../services/siteService';
import { DomainService } from '../../services/domainService';
import { AnalyticsService } from '../../services/analyticsService';
import { ActionButton, DataTable, ActionMenu, ConfirmDialog, StatCard, SiteTableRow, SiteTableHeader } from './common';
import { getDisplayUrl, getPreviewUrl } from '../../utils/urlHelper';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { sites, loading, error } = useSites();
  const [stats, setStats] = useState({
    totalSites: 0,
    publishedSites: 0,
    totalViews: 0,
  });
  const [domainConfigs, setDomainConfigs] = useState<Record<string, { isActive: boolean; basePath?: string } | null>>({});
  const [activeDomains, setActiveDomains] = useState<Record<string, string>>({});
  const { usersCache } = useUsersCache(sites);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; site: LandingPage } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<LandingPage | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useSnackbar();

  const displayUser = React.useMemo(() => {
    return currentUser || {
      id: 'guest',
      displayName: 'ゲストユーザー',
      email: 'guest@example.com'
    };
  }, [currentUser]);

  useEffect(() => {
    const loadDomainConfigs = async () => {
      const candidates = sites.filter(site => !!site.customDomain);
      if (candidates.length === 0) {
        setActiveDomains({});
        setDomainConfigs({});
        return;
      }

      try {
        const results = await Promise.all(
          candidates.map(async (site) => {
            try {
              const config = await DomainService.getDomain(site.id);
              return {
                siteId: site.id,
                domain: config && config.isActive ? config.domain : null,
                domainConfig: config ? { isActive: config.isActive, basePath: config.basePath } : null,
              };
            } catch (error) {
              return { siteId: site.id, domain: null, domainConfig: null };
            }
          })
        );

        const domainMap: Record<string, string> = {};
        const configMap: Record<string, { isActive: boolean; basePath?: string } | null> = {};
        results.forEach(({ siteId, domain, domainConfig }) => {
          if (domain) {
            domainMap[siteId] = domain;
          }
          configMap[siteId] = domainConfig;
        });
        setActiveDomains(domainMap);
        setDomainConfigs(configMap);
      } catch (error) {
        setActiveDomains({});
        setDomainConfigs({});
      }
    };

    if (sites.length > 0) {
      loadDomainConfigs();
    }
  }, [sites]);

  useEffect(() => {
    const loadStats = async () => {
      const totalSites = sites.length;
      const publishedSites = sites.filter(site => {
        const domainConfig = domainConfigs[site.id];
        return SiteService.getActualStatusSync(site, domainConfig) === 'published';
      }).length;

      let totalViews = 0;
      if (sites.length > 0) {
        try {
          const endDate = new Date();
          const startDate = new Date();
          startDate.setFullYear(startDate.getFullYear() - 1);

          const viewsPromises = sites.map(async (site) => {
            try {
              const sessions = await AnalyticsService.getSessions({
                siteId: site.id,
                startDate,
                endDate,
              });
              return sessions.length;
            } catch (error) {
              return 0;
            }
          });

          const viewsArray = await Promise.all(viewsPromises);
          totalViews = viewsArray.reduce((sum, views) => sum + views, 0);
        } catch (error) {
          totalViews = 0;
        }
      }

      setStats({
        totalSites,
        publishedSites,
        totalViews,
      });
    };

    loadStats();
  }, [sites, domainConfigs]);

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuDelete = (site: LandingPage) => {
    setSelectedSite(site);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleClearCache = async (site: LandingPage) => {
    handleMenuClose();
    
    if (!window.confirm('Cloudflareのキャッシュをクリアしますか？\nサイトの表示が最新の状態に更新されます。')) {
      return;
    }

    try {
      setActionLoading(true);
      const domainConfig = await DomainService.getDomain(site.id);
      if (!domainConfig) {
        showSnackbar('ドメイン設定が見つかりません', 'error');
        return;
      }
      await DomainService.purgeCache(domainConfig.zoneId);
      showSnackbar('✅ キャッシュを正常にクリアしました', 'success');
    } catch (error: any) {
      showSnackbar(error.message || 'キャッシュクリアに失敗しました', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePreviewSite = (site: LandingPage) => {
    const basePath = domainConfigs[site.id]?.basePath;
    const previewUrl = activeDomains[site.id]
      ? getDisplayUrl(activeDomains[site.id], basePath)
      : getPreviewUrl(site.id);

    window.open(previewUrl, '_blank');
    handleMenuClose();
  };

  const deleteSite = async (siteId: string) => {
    try {
      await SiteService.deleteSite(siteId);
    } catch (err) {
      console.error('サイトの削除に失敗しました:', err);
      throw err;
    }
  };

  const confirmDeleteSite = async () => {
    if (selectedSite) {
      try {
        setActionLoading(true);
        await deleteSite(selectedSite.id);
        showSnackbar('サイトを削除しました', 'success');
        // 削除後、サイト一覧を更新するためにページをリロード
        window.location.reload();
      } catch (error) {
        showSnackbar('サイトの削除に失敗しました', 'error');
      } finally {
        setActionLoading(false);
      }
    }
    setDeleteDialogOpen(false);
    setSelectedSite(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          再読み込み
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontSize: '32px', fontWeight: 600, color: '#000000' }}
      >
        ダッシュボード
      </Typography>
      <Typography 
        variant="body1" 
        color="textSecondary" 
        paragraph
        sx={{ fontSize: '16px', color: '#000000', mb: 4 }}
      >
        ようこそ、{displayUser.displayName || displayUser.email}さん
      </Typography>

      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)' 
          },
          gap: 2,
          mb: 3 
        }}
      >
        <StatCard
          title="総サイト数"
          value={stats.totalSites}
          icon={<WebIcon />}
          iconShape="circle"
        />
        <StatCard
          title="公開済みサイト"
          value={stats.publishedSites}
          icon={<TrendingUpIcon />}
          iconShape="circle"
        />
        <StatCard
          title="総閲覧数"
          value={stats.totalViews}
          icon={<ViewIcon />}
          iconShape="circle"
        />
      </Box>

      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ mb: 2, mt: 4, fontWeight: 600, fontSize: '16px', color: '#000000' }}
      >
        最近更新したサイト
      </Typography>
      <DataTable>
        <SiteTableHeader />
              <TableBody>
                {sites.slice().sort((a, b) => {
                  const timeA = a.updatedAt?.getTime() || 0;
                  const timeB = b.updatedAt?.getTime() || 0;
                  return timeB - timeA;
          }).slice(0, 5).map((site) => (
            <SiteTableRow
              key={site.id}
              site={site}
              domainConfig={domainConfigs[site.id]}
              activeDomain={activeDomains[site.id]}
              usersCache={usersCache}
              currentUser={currentUser}
              onEdit={(site) => navigate(`/admin/sites/${site.id}/edit`)}
              onAnalytics={(site) => navigate(`/admin/sites/${site.id}/analytics`)}
              onMenuOpen={(e, site) => setMenuAnchor({ element: e.currentTarget, site })}
              showEditButton={true}
            />
          ))}
              </TableBody>
      </DataTable>
      <ActionMenu
        anchorEl={menuAnchor?.element || null}
        open={!!menuAnchor}
        onClose={handleMenuClose}
        items={[
          {
            label: 'プレビュー',
            icon: <ViewIcon sx={{ fontSize: '18px', color: '#616161' }} />,
            onClick: () => menuAnchor && handlePreviewSite(menuAnchor.site),
          },
          {
            label: 'キャッシュクリア',
            icon: <CleaningServicesIcon sx={{ fontSize: '18px', color: '#616161' }} />,
            onClick: () => menuAnchor && handleClearCache(menuAnchor.site),
          },
          {
            label: '削除',
            icon: <DeleteIcon sx={{ fontSize: '18px', color: '#D32F2F' }} />,
            onClick: () => menuAnchor && handleMenuDelete(menuAnchor.site),
            color: '#D32F2F',
          },
        ]}
      />

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedSite(null);
        }}
        onConfirm={confirmDeleteSite}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
